/**
 * One-click starting points for the model catalog ("Start from").
 *
 * **A preset is a shortcut, not a new kind of filter.** Each one is a list of
 * ordinary `CatalogFilter` conditions, written with the same ids the filter
 * builder mints (`f:<field>`). Clicking one puts those conditions in the filter
 * row, where they read and edit like any other chip. The page remembers which
 * preset was clicked and the row it produced (`PickedPreset`); changing any
 * chip after that un-highlights it.
 *
 * **Why beside `catalogFilters.ts`, not in `lib/`.** The conditions are this
 * page's filter type. Putting the presets in `lib/` would make `lib/` import a
 * page module, which runs the layering backwards.
 *
 * **What a preset replaces.** Every condition except the country of origin
 * toggle, the available-models toggle and the name search. The two toggles
 * are cuts the operator set on purpose, and the name box is what they are
 * typing; all three survive. Everything
 * else goes, so a preset never inherits a hidden constraint from before.
 */
import type { AgentModelBar } from '@/lib/api'
import type { CatalogFilter } from './catalogFilters'

export type CatalogPresetId =
  | 'fitsEveryAgent'
  | 'curated'
  | 'frontier'
  | 'inUse'

export interface CatalogPreset {
  id: CatalogPresetId
  label: string
  /** Tooltip: what the preset picks, in plain words. */
  hint: string
  conditions: readonly CatalogFilter[]
}

/** The conditions a preset leaves alone when it is applied. */
function isKept(f: CatalogFilter): boolean {
  return f.field === 'origin' || f.field === 'name' || f.field === 'availability'
}

/** "Fits every agent", built from the planner and generator's bar rather than
 * restated here, so the preset and the "Fits the <Agent>" chip on the
 * Assignments page can never disagree. That bar is the strictest (the largest
 * context, and every capability any agent needs), so a model that meets it
 * fits every agent. */
function fitsEveryAgentConditions(bar: AgentModelBar): CatalogFilter[] | null {
  // A capability this page has no filter for (a newer backend added one) would
  // be silently dropped, and the preset would then recommend models the
  // Assignments page says miss the bar. Leave the preset out instead.
  if (bar.needs.some(n => n !== 'toolCalling' && n !== 'reasoning' && n !== 'imageInput')) return null
  const out: CatalogFilter[] = []
  if (bar.needs.includes('toolCalling')) out.push({ id: 'f:toolCalling', field: 'toolCalling', values: ['yes'] })
  if (bar.needs.includes('reasoning')) out.push({ id: 'f:reasoning', field: 'reasoning', values: ['yes'] })
  if (bar.needs.includes('imageInput')) out.push({ id: 'f:vision', field: 'vision', values: ['yes'] })
  if (bar.minContextTokens > 0) {
    out.push({ id: 'f:context', field: 'context', op: 'atLeast', value: bar.minContextTokens })
  }
  return out
}

/**
 * The presets, in display order. `strictestBar` is the generator's bar from the
 * app settings response; without it (an older backend, or the settings failed
 * to load) "Fits every agent" is left out rather than guessed.
 *
 * Deliberately few. Presets that asked the operator to know which capability
 * matters ("uses tools", "reads screenshots") were dropped: the agent bar
 * already answers that.
 */
export function catalogPresets(strictestBar: AgentModelBar | undefined): CatalogPreset[] {
  const presets: CatalogPreset[] = []
  const fitsEvery = strictestBar ? fitsEveryAgentConditions(strictestBar) : null
  if (fitsEvery) {
    presets.push({
      id: 'fitsEveryAgent',
      label: 'Fits every agent',
      hint: 'Models that meet what every agent needs: tool use, reasoning, vision and a large context.',
      conditions: fitsEvery,
    })
  }
  presets.push(
    {
      id: 'curated',
      label: 'Otto Curated',
      hint: 'Our hand-picked shortlist.',
      conditions: [{ id: 'f:shortlist', field: 'shortlist', values: ['otto-curated'] }],
    },
    {
      id: 'frontier',
      label: 'Frontier',
      hint: "Each supplier's strongest current models.",
      conditions: [{ id: 'f:shortlist', field: 'shortlist', values: ['frontier'] }],
    },
    {
      id: 'inUse',
      label: 'In use',
      hint: 'Models an agent has used.',
      conditions: [{ id: 'f:status', field: 'status', values: ['inUse'] }],
    },
  )
  return presets
}

/** The filter list after clicking a preset: origin and name kept, everything
 * else replaced by the preset's conditions. */
export function applyPreset(filters: readonly CatalogFilter[], preset: CatalogPreset): CatalogFilter[] {
  return [...filters.filter(isKept), ...preset.conditions]
}

/** The filter list after clicking the highlighted preset again: its conditions
 * cleared, origin and name kept. */
export function clearPreset(filters: readonly CatalogFilter[]): CatalogFilter[] {
  return filters.filter(isKept)
}

/** A stable text form of one condition, for comparing two lists. Multi-value
 * conditions compare as sets, so "Low or Medium" equals "Medium or Low". */
function conditionKey(f: CatalogFilter): string {
  if ('values' in f) return `${f.field}:${[...f.values].sort().join('|')}`
  if ('op' in f) return `${f.field}:${f.op}:${f.value}`
  return `${f.field}:${f.text}`
}

/** Whether two filter lists hold the same conditions, in any order. */
export function sameFilters(a: readonly CatalogFilter[], b: readonly CatalogFilter[]): boolean {
  const ak = a.map(conditionKey).sort()
  const bk = b.map(conditionKey).sort()
  return ak.length === bk.length && ak.every((k, i) => k === bk[i])
}

/** The preset an operator clicked, and the whole filter row it produced.
 * Kept rather than worked out from the chips: two presets can share chips, so
 * reading the highlight off the chips could light one the operator never
 * picked. */
export interface PickedPreset {
  id: CatalogPresetId
  filters: readonly CatalogFilter[]
}

/** The highlighted preset: the one picked, as long as the filter row is still
 * exactly what picking it produced. Any chip added, removed or edited since,
 * the name search and origin toggle included, clears the highlight. */
export function activePresetId(
  picked: PickedPreset | null,
  filters: readonly CatalogFilter[],
): CatalogPresetId | null {
  if (!picked) return null
  return sameFilters(picked.filters, filters) ? picked.id : null
}
